import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {GlobalService} from 'app/global/global.service';
import {combineLatest, of, switchMap} from 'rxjs';
import {catchError, map, take, tap} from 'rxjs/operators';
import {ContentLibraryService} from '../contentlibrary.service';
import * as ContentLibraryActions from './content-library-store.action';
import {ContentLibrary, Daum} from './content-library-store.dto';
import * as ContentLibrarySelector from './content-library-store.selector';

@Injectable({
    providedIn:'root'
})
export class ContentLibraryEffects{
    constructor(
        private _actions$: Actions,
        private _contentLibraryService: ContentLibraryService,
        private globalService:GlobalService,
        private _store:Store
    ) {}

    fetchContentLibrary$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                ContentLibraryActions.fetchContentLibrary,
                ContentLibraryActions.searchChange,
                ContentLibraryActions.changePage
            ),
            switchMap(() => {
                return combineLatest({
                    search: this._store.select(ContentLibrarySelector.search),
                    paging: this._store.select(ContentLibrarySelector.paging),
                }).pipe(take(1))
            }),
            switchMap(({search, paging}) => {
                return this._contentLibraryService.fetchContents(paging.currentPage || '' ,search)
                    .pipe(
                        map((contents:ContentLibrary) => ContentLibraryActions.fetchContentLibrarySuccess(contents)),
                        catchError(error => of(ContentLibraryActions.fetchContentLibraryFailure(error)))
                    )
            })
        )
    });

    fetchAllContentLibraries$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                ContentLibraryActions.fetchAllContentLibrary,
                ContentLibraryActions.searchChange,
            ),
            switchMap((page) => {
                return combineLatest({
                    search: this._store.select(ContentLibrarySelector.search),
                    page : of((page as unknown as {page:number}).page)
                }).pipe(take(1))
            }),
            switchMap(({search, page}) => {
                return this._contentLibraryService.fetchContents(page || '' ,search)
                    .pipe(
                        map((allContents:Daum[]) => ContentLibraryActions.fetchAllContentLibrarySuccess(allContents)),
                        catchError(error => of(ContentLibraryActions.fetchContentLibraryFailure(error)))
                    )
            })
        )
    });


    addContentLibrary$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(ContentLibraryActions.addContentLibrary),
            switchMap(({formData}) => {
                return this._contentLibraryService.createContent(formData)
                    .pipe(
                        map((response) => ContentLibraryActions.addContentLibrarySuccess(response)),
                        catchError(error => of(ContentLibraryActions.addContentLibraryFailure(error)))
                    )
            })
        )
    });

    updateContentLibrary$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(ContentLibraryActions.updateContentLibrary),
            switchMap(({formData}) => {
                return this._contentLibraryService.updateContent(formData)
                    .pipe(
                        map((response) => ContentLibraryActions.updateContentLibrarySuccess(response)),
                        catchError(error => of(ContentLibraryActions.updateContentLibraryFailure(error)))
                    )
            })
        )
    });

    deleteContentLibrary$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(ContentLibraryActions.deleteContent),
            switchMap(({id}) => {
                return this._contentLibraryService.removeContent(id)
                    .pipe(
                        map(() => ContentLibraryActions.deleteContentLibrarySuccess(id)),
                        catchError(error => of(ContentLibraryActions.deleteContentLibraryFailure(error)))
                    )
            })
        )
    });

    handleContentLibraryFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                ContentLibraryActions.fetchContentLibraryFailure,
                ContentLibraryActions.addContentLibraryFailure,
                ContentLibraryActions.updateContentLibraryFailure,
                ContentLibraryActions.deleteContentLibraryFailure,
            ),
            tap(args => {
                this.globalService.toastrError(args.error);
            })
        )
    }, {dispatch: false})
}
